/**
 * 我的选号 — 智能提醒列表
 *
 * 显示用户保存过的所有选号 + 命中状态
 * 命中行高亮(金色边框 + 🎉 角标)
 */
import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, Trash2, ChevronLeft, Filter, Loader2, Sparkles,
  CheckCircle2, XCircle, Clock, Calendar, Tag, AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../lib/api.js';

const STATUS_META = {
  pending:  { label: '待开奖', icon: Clock,        color: 'text-brand-cyan',  bg: 'bg-brand-cyan/10',  border: 'border-brand-cyan/30' },
  hit:      { label: '已命中', icon: CheckCircle2, color: 'text-brand-gold',  bg: 'bg-brand-gold/10',  border: 'border-brand-gold/40' },
  missed:   { label: '未命中', icon: XCircle,      color: 'text-ink-3',       bg: 'bg-white/5',        border: 'border-white/10' },
  expired:  { label: '已过期', icon: AlertCircle,  color: 'text-ink-3',       bg: 'bg-white/5',        border: 'border-white/10' },
};

const FILTERS = [
  { v: 'all',      label: '全部' },
  { v: 'pending',  label: '⏳ 待开奖' },
  { v: 'hit',      label: '🎉 已命中' },
  { v: 'missed',   label: '❌ 未命中' },
  { v: 'expired',  label: '⌛ 已过期' },
];

export default function MyPicksPage() {
  const [selections, setSelections] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, hit: 0, missed: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.selections.list();
      if (r?.ok) {
        setSelections(r.selections || []);
        setStats(r.stats || {});
      }
    } catch (e) {
      toast.error('加载失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!confirm('确认删除这条选号?')) return;
    setDeletingId(id);
    try {
      const r = await api.selections.remove(id);
      if (r?.ok) {
        toast.success('已删除');
        load();
      } else {
        toast.error(r?.error || '删除失败');
      }
    } catch (e) {
      toast.error('删除失败: ' + e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return selections;
    return selections.filter(s => s.status === filter);
  }, [selections, filter]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link
            to="/profile"
            className="text-ink-3 hover:text-ink-0 transition p-1 -ml-1"
            title="返回"
          >
            <ChevronLeft size={18} />
          </Link>
          <h2 className="text-base font-bold flex items-center gap-2" style={{ color: 'var(--ink-0)' }}>
            <Bell size={16} className="text-brand-cyan" />
            智能提醒
          </h2>
        </div>
        <button onClick={load} className="btn-ghost text-[10px]">↻ 刷新</button>
      </div>

      {/* 命中横幅(如果有) */}
      {stats.hit > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="card !p-2.5 bg-gradient-to-r from-brand-gold/15 to-brand-rose/15 border border-brand-gold/40 flex items-center gap-2"
        >
          <Sparkles size={16} className="text-brand-gold" />
          <div className="flex-1">
            <div className="text-xs font-bold text-brand-gold">
              🎉 您已有 {stats.hit} 注命中!记得及时兑奖
            </div>
            <div className="text-[10px] text-ink-2">点击下方"已命中"查看详情</div>
          </div>
        </motion.div>
      )}

      {/* 统计 */}
      <div className="grid grid-cols-5 gap-1.5">
        <StatBox label="总" value={stats.total} color="text-ink-1" />
        <StatBox label="待开" value={stats.pending} color="text-brand-cyan" />
        <StatBox label="命中" value={stats.hit} color="text-brand-gold" />
        <StatBox label="未中" value={stats.missed} color="text-ink-3" />
        <StatBox label="过期" value={stats.expired} color="text-ink-3" />
      </div>

      {/* 筛选 */}
      <div className="card !p-2 flex items-center gap-1.5 overflow-x-auto">
        {FILTERS.map(f => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={clsx(
              'px-2.5 py-1 rounded text-xs whitespace-nowrap border transition',
              filter === f.v
                ? 'bg-brand-cyan/20 border-brand-cyan/40 text-brand-cyan'
                : 'bg-white/5 border-white/10 text-ink-2 hover:border-white/20'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* 列表 */}
      {loading ? (
        <div className="card text-center py-8 text-ink-2 text-xs">
          <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
          加载中...
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-10 text-ink-3 text-xs space-y-2">
          <Bell size={32} className="mx-auto opacity-30" />
          <div>{filter === 'all' ? '还没有保存的选号' : '该状态下没有选号'}</div>
          {filter === 'all' && (
            <Link to="/pick" className="btn-primary inline-flex items-center gap-1.5 mt-2 !text-xs">
              <Sparkles size={12} /> 去选号
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence>
            {filtered.map(s => (
              <SelectionCard
                key={s.id}
                s={s}
                onDelete={() => handleDelete(s.id)}
                deleting={deletingId === s.id}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="card !p-2 text-center">
      <div className="text-[10px] text-ink-3">{label}</div>
      <div className={clsx('text-lg font-bold font-mono', color)}>{value ?? '-'}</div>
    </div>
  );
}

function SelectionCard({ s, onDelete, deleting }) {
  const meta = STATUS_META[s.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const isHit = s.status === 'hit';
  const canDelete = s.status === 'pending';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className={clsx(
        'card !p-2.5 border transition',
        meta.border,
        isHit && 'bg-gradient-to-r from-brand-gold/10 to-transparent shadow-lg shadow-brand-gold/10'
      )}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <div className={clsx('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0', meta.bg)}>
          <Icon size={14} className={meta.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-semibold', meta.bg, meta.color)}>
              {meta.label}
            </span>
            <span className="text-[10px] text-ink-3 flex items-center gap-0.5">
              <Calendar size={9} /> 期号 {s.period}
            </span>
            <span className="text-[10px] text-ink-3 flex items-center gap-0.5">
              <Tag size={9} /> {s.pick_count} 注
            </span>
            {s.pool_level && s.pool_level !== 'manual' && (
              <span className="text-[10px] text-ink-3 bg-white/5 px-1.5 py-0.5 rounded">
                {s.pool_level}
              </span>
            )}
            {isHit && s.hit_pick && (
              <span className="text-[10px] text-brand-gold font-bold bg-brand-gold/20 px-1.5 py-0.5 rounded">
                🎯 命中 {s.hit_pick}
              </span>
            )}
            {canDelete && (
              <button
                onClick={onDelete}
                disabled={deleting}
                className="ml-auto text-ink-3 hover:text-red-400 p-0.5 disabled:opacity-30"
                title="删除"
              >
                {deleting ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
              </button>
            )}
          </div>
          {s.note && (
            <div className="text-[10px] text-ink-2 mt-1 line-clamp-1">{s.note}</div>
          )}
        </div>
      </div>

      {/* 号码预览(最多 8 个 + ...更多) */}
      <div className="flex flex-wrap gap-1 mt-1.5">
        {s.picks.slice(0, 8).map((p, i) => {
          const isHitCode = isHit && p === s.hit_pick;
          return (
            <span
              key={i}
              className={clsx(
                'font-mono text-xs px-1.5 py-0.5 rounded',
                isHitCode
                  ? 'bg-brand-gold/30 text-brand-gold font-bold ring-1 ring-brand-gold/50'
                  : 'bg-white/5 text-ink-1'
              )}
            >
              {p}
            </span>
          );
        })}
        {s.picks.length > 8 && (
          <span className="text-[10px] text-ink-3 px-1.5 py-0.5">
            +{s.picks.length - 8}
          </span>
        )}
      </div>

      {/* 时间 */}
      <div className="text-[10px] text-ink-3 mt-1.5 flex items-center gap-1.5">
        <span>保存于 {fmtTime(s.created_at)}</span>
        {s.hit_at && (
          <span>· 命中于 {fmtTime(s.hit_at)}</span>
        )}
      </div>
    </motion.div>
  );
}

function fmtTime(ts) {
  if (!ts) return '-';
  const d = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
  return d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}
